import {defineStore, storeToRefs} from 'pinia'
import { type Ref, type ComputedRef, ref, computed } from 'vue'
import type { DocumentVerification } from '~/libs/document-intelligence-types'
import { useDocumentsStore } from '~/store/documents'

export const useDocumentIntelligenceStore = defineStore('documentIntelligence', () => {
  const documentsStore = useDocumentsStore()

  const { documents } = storeToRefs(documentsStore) as { documents: Ref<{ id: number }[]> }

  /*
   * STATE
  */
  const documentVerifications: Ref<DocumentVerification[]> = ref([])
  const documentVerificationsEnabled: Ref<boolean | null> = ref(null)
  const documentIds = ref<number[]>([])

  /*
   * GETTERS
  */

  const currentDocumentIds = computed(() => {
    const documentStoreDocIds = documents.value.map(d => d.id)
    return new Set([...documentStoreDocIds, ...documentIds.value])
  })

  const activeDocumentVerifications: ComputedRef<DocumentVerification[]> = computed(() => {
    return documentVerifications.value.filter(v => currentDocumentIds.value.has(v.documentId))
  })

  /*
   * ACTIONS
  */
  function setDocumentVerifications(verifications: DocumentVerification[]) {
    documentVerifications.value = verifications
  }

  function updateDocumentVerification(verification: DocumentVerification) {
    const existingIndex = documentVerifications.value.findIndex(v => v.documentId === verification.documentId)
    if (existingIndex !== -1) {
      documentVerifications.value[existingIndex] = verification
      return
    }

    documentVerifications.value.push(verification)
  }

  function setDocumentVerificationsEnabled(enabled = false) {
    documentVerificationsEnabled.value = enabled
  }

  function setDocumentIds(ids: number[]) {
    documentIds.value = ids
  }

  function removeVerification(docId: number) {
    documentIds.value = documentIds.value.filter(id => id !== docId)
    documentVerifications.value = documentVerifications.value.filter(verification => verification.documentId !== docId)
  }

  return {
    // STATE
    documentVerifications,
    documentVerificationsEnabled,
    documentIds,
    // GETTERS
    currentDocumentIds,
    activeDocumentVerifications,
    // ACTIONS
    setDocumentVerifications,
    updateDocumentVerification,
    setDocumentVerificationsEnabled,
    setDocumentIds,
    removeVerification
  }
})
